import { useState, useEffect } from "react"
import { Business, Portfolio as PortfolioZod } from "@/schemas" // Using Zod types
import { useFirebase } from "@/context/FirebaseProvider" // Replaced useDataUtils and useData
import { getAuth } from "firebase/auth"
import { collection, query, where, getDocs } from "firebase/firestore"
import { db } from "@/utils/firebase"

/**
 * Hook to get businesses that the user is a member of but are not in any of the user's portfolios
 * These businesses will be shown in a "No Portfolio" section
 */
export const useNoPortfolioBusinesses = (searchTermParam?: string) => { // Added searchTermParam
  const [noPortfolioBusinesses, setNoPortfolioBusinesses] = useState<
    Business[]
  >([])
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { userData, portfolios: userPortfoliosFromFirebase, businesses: userBusinessesFromFirebase } = useFirebase() // Get data from FirebaseProvider
  const auth = getAuth()
  const currentUserId = auth.currentUser?.uid
  const searchTerm = searchTermParam || "" // Use searchTermParam

  // Fetch businesses directly from Firestore where the user is a member
  // but not a member of the portfolio the business belongs to
  useEffect(() => {
    const fetchNoPortfolioBusinesses = async () => {
      if (!currentUserId) {
        setNoPortfolioBusinesses([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        // Step 1: Get all businesses where the user is a member
        const businessesRef = collection(db, "businesses")
        const querySnapshot = await getDocs(businessesRef)
        const userBusinesses: Business[] = []

        querySnapshot.forEach((doc) => {
          const businessData = doc.data() as Business
          businessData.id = doc.id

          // Check if the business has members
          if (businessData.members && Array.isArray(businessData.members)) {
            // Find the user's membership in this business
            const userMembership = businessData.members.find(
              (member) => member.userId === currentUserId
            )

            if (userMembership && userMembership.role !== "Primary Owner") {
              userBusinesses.push(businessData)
            }
          }
        })

        // Step 2: Get all portfolios where the user is a member
        // userPortfoliosFromFirebase already contains portfolios the user has access to.
        const userPortfolioIds = new Set<string>(userPortfoliosFromFirebase?.map(p => p.id) || []);

        // Step 3: Filter businesses where user is not a member of the portfolio
        // This step assumes userBusinesses (from Step 1) contains all businesses the user is a member of.
        const businessesToList = userBusinesses.filter((business) => {
          const isInUserPortfolio =
            business.portfolioId && userPortfolioIds.has(business.portfolioId)

          return !isInUserPortfolio
        })
        setNoPortfolioBusinesses(businessesToList)
      } catch (error) {
        console.error("Error fetching businesses:", error)
        setNoPortfolioBusinesses([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchNoPortfolioBusinesses()
  }, [currentUserId, userPortfoliosFromFirebase]) // Added userPortfoliosFromFirebase dependency

  // Filter businesses based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = noPortfolioBusinesses.filter((business) => {
        const nameMatch = business.name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
        const addressMatch = business.address
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
        return nameMatch || addressMatch
      })
      setFilteredBusinesses(filtered)
    } else {
      setFilteredBusinesses(noPortfolioBusinesses)
    }
  }, [noPortfolioBusinesses, searchTerm])

  // Create a dummy portfolio object to hold these businesses
  // This is a client-side construct, so define a specific type for it.
  // It should align with the PortfolioZod type for properties accessed by PortfolioList
  interface DummyPortfolioForDisplay {
    id: string;
    name: string;
    businesses?: Business[]; // Kept for clarity, but PortfolioList uses businessRefs
    businessRefs: string[] | null; // Match Portfolio schema
    members: PortfolioZod['members']; // Match Portfolio schema for type consistency
    createdAt: PortfolioZod['createdAt']; // Match Portfolio schema
    updatedAt: PortfolioZod['updatedAt']; // Match Portfolio schema
    // Add other fields from PortfolioZod if they cause type errors downstream
    // For example, if PortfolioCard expects `type`, `billingInfo`, etc.
    // Making it compatible enough for PortfolioList.tsx
    type?: PortfolioZod['type'];
    billingInfo?: PortfolioZod['billingInfo'];
    primaryOwnerRef?: PortfolioZod['primaryOwnerRef'];
    secondaryOwnerRefs?: PortfolioZod['secondaryOwnerRefs'];
    managerRefs?: PortfolioZod['managerRefs'];
    lastUpdated?: PortfolioZod['lastUpdated'];
    owner?: PortfolioZod['owner'];
  }

  const createNoPortfolioDummy = (): DummyPortfolioForDisplay => {
    const now = new Date();

    return {
      id: "no-portfolio",
      name: "No Portfolio",
      // businesses: filteredBusinesses, // PortfolioList uses businessRefs to find businesses in allBusinesses
      businessRefs: filteredBusinesses.map(b => b.id).filter(id => id !== undefined) as string[], // Populate with IDs from filteredBusinesses
      members: null, // Not strictly needed by PortfolioList for the dummy, but good for type alignment
      createdAt: now, // Assign Date object directly
      updatedAt: now, // Assign Date object directly
      // Optional fields can be undefined or null
      type: null,
      billingInfo: null,
      primaryOwnerRef: null,
      secondaryOwnerRefs: null,
      managerRefs: null,
      lastUpdated: null,
      owner: null,
    };
  };

  return {
    noPortfolioBusinesses: filteredBusinesses,
    noPortfolioDummy: createNoPortfolioDummy(),
    hasNoPortfolioBusinesses: filteredBusinesses.length > 0,
    isLoading,
  }
}
